
import Vue from 'vue'
import store from '@/store'
import { GroupChannelHandler } from '@sendbird/chat/groupChannel'

export default Vue.extend({
	name: 'ChatRoom',
	data() {
		return {
			loading: false,
			searchChannel: '',
			messageInput: '',
			showMobileChannels: false,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			selectedChannel: null as any,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			groupChannels: [] as any[],
			deleteDialog: false,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			channelToDelete: null as any,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			messages: [] as any[],
			messagesLoading: false,
			channelHandlerId: 'CHAT_ROOM_HANDLER',
			contextMenu: {
				show: false,
				x: 0,
				y: 0,
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				channel: null as any
			}
		}
	},
	computed: {
		user() {
			return store.state.user;
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		filteredChannels(): any[] {
			if (!this.searchChannel.trim()) {
				return this.groupChannels
			}
			const searchTerm = this.searchChannel.trim().toLowerCase()
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			return this.groupChannels.filter((channel: any) => {
				const channelName = this.getChannelName(channel).toLowerCase()
				return channelName.includes(searchTerm)
			})
		},
		channelToDeleteName(): string {
			if (!this.channelToDelete) return ''
			return this.getChannelName(this.channelToDelete)
		}
	},
	methods: {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		async selectChannel(channel: any) {
			// 如果點選的是當前頻道，不重新載入
			if (this.selectedChannel?.url === channel.url) return

			this.selectedChannel = channel
			await this.loadMessages()
			// 標記訊息為已讀
			await this.markAsRead()
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		async selectChannelMobile(channel: any) {
			this.showMobileChannels = false
			await this.selectChannel(channel)
		},
		async markAsRead() {
			if (!this.selectedChannel) return
			try {
				await this.selectedChannel.markAsRead()
				// 更新左側頻道列表的未讀數量
				const channelIndex = this.groupChannels.findIndex(
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(c: any) => c.url === this.selectedChannel.url
				)
				if (channelIndex !== -1) {
					this.$set(this.groupChannels[channelIndex], 'unreadMessageCount', 0)
				}
			} catch (error) {
				console.error('Failed to mark as read:', error)
			}
		},
		setupChannelHandler() {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const sb = this.$sendbird.get() as any
			if (!sb) return

			// 移除舊的 handler
			sb.groupChannel.removeGroupChannelHandler(this.channelHandlerId)

			// 建立新的 handler（使用 GroupChannelHandler 類別）
			const channelHandler = new GroupChannelHandler({
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				onMessageReceived: (channel: any, message: any) => {
					// 檢查這個 channel 是否已經在列表中
					const channelIndex = this.groupChannels.findIndex(
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						(c: any) => c.url === channel.url
					)

					// 如果是新 channel（不在列表中），加入列表最上方
					if (channelIndex === -1) {
						this.groupChannels.unshift(channel)
					} else if (channelIndex > 0) {
						// 已存在的 channel 移到最上方
						const [movedChannel] = this.groupChannels.splice(channelIndex, 1)
						this.groupChannels.unshift(movedChannel)
					}

					// 更新最上方頻道的最新訊息
					this.$set(this.groupChannels[0], 'lastMessage', message)

					if (this.selectedChannel?.url === channel.url) {
						// 目前選中的頻道：加入訊息並標記已讀
						this.messages.push(this.transformMessage(message))
						this.$nextTick(() => {
							this.scrollToBottom()
						})
						// 標記訊息為已讀
						this.markAsRead()
					} else {
						// 其他頻道：使用 channel 的實際未讀數量
						this.$set(this.groupChannels[0], 'unreadMessageCount', channel.unreadMessageCount)
					}
				},
				// 監聯新 channel 邀請（當其他用戶建立包含我的 channel 時）
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				onUserReceivedInvitation: (channel: any, inviter: any, invitees: any) => {
					// 檢查自己是否在被邀請名單中
					const myId = this.user.userDto?.id
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					const isInvited = invitees.some((invitee: any) => invitee.userId === myId)

					if (isInvited) {
						// 檢查 channel 是否已經存在於列表中
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						const exists = this.groupChannels.some((c: any) => c.url === channel.url)
						if (!exists) {
							// 將新 channel 加入列表最上方
							this.groupChannels.unshift(channel)
						}
					}
				}
			})

			sb.groupChannel.addGroupChannelHandler(this.channelHandlerId, channelHandler)
		},
		async loadMessages() {
			if (!this.selectedChannel) return

			this.messagesLoading = true
			this.messages = []

			try {
				// 建立訊息查詢
				const listQuery = this.selectedChannel.createPreviousMessageListQuery({
					limit: 50,
					reverse: false
				})

				// 載入訊息
				const sendbirdMessages = await listQuery.load()

				// 轉換訊息格式
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				this.messages = sendbirdMessages.map((msg: any) => this.transformMessage(msg))
			} catch (error) {
				console.error('Failed to load messages:', error)
			} finally {
				this.messagesLoading = false
				// 等待 DOM 更新後捲動到底部
				this.$nextTick(() => {
					this.scrollToBottom()
				})
			}
		},
		scrollToBottom() {
			const container = this.$refs.messagesContainer as HTMLElement
			if (container) {
				container.scrollTop = container.scrollHeight
			}
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		transformMessage(msg: any) {
			return {
				id: msg.messageId,
				content: msg.message,
				timestamp: new Date(msg.createdAt),
				isMe: msg.sender?.userId === this.user.userDto?.id,
				senderName: msg.sender?.nickname || msg.sender?.userId,
				senderAvatar: msg.sender?.profileUrl || '',
				isRead: true
			}
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		openContextMenu(event: MouseEvent, channel: any) {
			this.contextMenu.show = false
			this.$nextTick(() => {
				this.contextMenu.x = event.clientX
				this.contextMenu.y = event.clientY
				this.contextMenu.channel = channel
				this.contextMenu.show = true
			})
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		openDeleteDialog(channel: any) {
			this.channelToDelete = channel
			this.deleteDialog = true
		},
		async confirmDeleteChannel() {
			if (!this.channelToDelete) return

			const deletedChannelUrl = this.channelToDelete.url
			const channelToLeave = this.channelToDelete
			const wasSelected = this.selectedChannel?.url === deletedChannelUrl

			// 先關閉彈窗，顯示 loading
			this.deleteDialog = false
			this.channelToDelete = null
			this.loading = true

			try {
				// 使用 Sendbird SDK 刪除/離開頻道
				await channelToLeave.leave()

				// 從列表中移除
				this.groupChannels = this.groupChannels.filter(
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(c: any) => c.url !== deletedChannelUrl
				)

				// 如果刪除的是目前選中的頻道，選擇第一個頻道
				if (wasSelected) {
					if (this.groupChannels.length > 0) {
						await this.selectChannel(this.groupChannels[0])
					} else {
						this.selectedChannel = null
						this.messages = []
					}
				}
			} catch (error) {
				console.error('Failed to delete channel:', error)
			} finally {
				this.loading = false
			}
		},
		handleEnterKey(event: KeyboardEvent) {
			// 如果正在使用輸入法選字，不送出訊息
			if (event.isComposing) return
			this.sendMessage()
		},
		async sendMessage() {
			if (!this.messageInput.trim() || !this.selectedChannel) return

			const messageText = this.messageInput.trim()
			this.messageInput = ''

			try {
				// 使用 Sendbird SDK 發送訊息
				const params = {
					message: messageText
				}
				const sentMessage = await this.selectedChannel.sendUserMessage(params)

				// 將發送的訊息加入列表
				const userDto = this.user.userDto
				const senderName = userDto?.firstName && userDto?.lastName
					? `${userDto.firstName} ${userDto.lastName}`
					: userDto?.firstName || userDto?.id || ''

				const newMessage = {
					id: sentMessage.messageId,
					content: messageText,
					timestamp: new Date(sentMessage.createdAt || Date.now()),
					isMe: true,
					senderName,
					senderAvatar: userDto?.avatarBlobId || '',
					isRead: false
				}
				this.messages.push(newMessage)

				// 將當前頻道移到列表最上方
				const channelIndex = this.groupChannels.findIndex(
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(c: any) => c.url === this.selectedChannel.url
				)
				if (channelIndex > 0) {
					const [channel] = this.groupChannels.splice(channelIndex, 1)
					this.groupChannels.unshift(channel)
				}

				// 捲動到底部
				this.$nextTick(() => {
					this.scrollToBottom()
				})
			} catch (error) {
				console.error('Failed to send message:', error)
				// 發送失敗時還原輸入
				this.messageInput = messageText
			}
		},
		formatTime(date: Date): string {
			return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
		},
		formatDate(date: Date): string {
			const today = new Date()
			const yesterday = new Date(today)
			yesterday.setDate(yesterday.getDate() - 1)

			if (date.toDateString() === today.toDateString()) {
				return String(this.$t('chatRoom.today'))
			} else if (date.toDateString() === yesterday.toDateString()) {
				return String(this.$t('chatRoom.yesterday'))
			}
			return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
		},
		showDateSeparator(index: number): boolean {
			if (index === 0) return true
			const currentDate = this.messages[index].timestamp.toDateString()
			const prevDate = this.messages[index - 1].timestamp.toDateString()
			return currentDate !== prevDate
		},
		// 判斷是否顯示對方的頭像和名字（連續訊息只顯示第一則）
		showSenderInfo(index: number): boolean {
			// 第一則訊息一定顯示
			if (index === 0) return true
			// 如果有日期分隔線，顯示
			if (this.showDateSeparator(index)) return true
			// 如果上一則是自己發的，這則是對方發的，顯示
			const prevMessage = this.messages[index - 1]
			if (prevMessage.isMe) return true
			// 其他情況（對方連續發話）不顯示
			return false
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		getChannelName(channel: any): string {
			// 顯示對方的 nickname
			const members = channel.members || []
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const otherMembers = members.filter((m: any) => m.userId !== this.user.userDto?.id)
			if (otherMembers.length > 0) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				return otherMembers.map((m: any) => m.nickname || m.userId).join(', ')
			}
			// 如果沒有其他成員，fallback 到 channel name
			if (channel.name) return channel.name
			return 'Unknown'
		},
		formatLastMessageTime(timestamp: number | undefined): string {
			if (!timestamp) return ''
			const date = new Date(timestamp)
			const now = new Date()
			const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
			const yesterday = new Date(today)
			yesterday.setDate(yesterday.getDate() - 1)
			const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())

			if (messageDate.getTime() === today.getTime()) {
				return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
			} else if (messageDate.getTime() === yesterday.getTime()) {
				return String(this.$t('chatRoom.yesterday'))
			} else if (now.getTime() - date.getTime() < 7 * 24 * 60 * 60 * 1000) {
				return date.toLocaleDateString('en-US', { weekday: 'long' })
			}
			return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
		},
		async getGroupChannelByUserId() {
			const userId = this.user.userDto?.id as string
			if (!userId) {
				console.error('User ID is not available')
				return
			}

			try {
				// 取得 session token
				const tokenResponse = await this.api$.sendbirdApi.issueSessionToken(userId)
				const sessionToken = tokenResponse.data.data?.token

				// 使用 token 連接到 Sendbird
				await this.$sendbird.connect(userId, sessionToken)

				// 取得 Sendbird 實例
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const sb = this.$sendbird.get() as any
				if (!sb) return

				// 取得群組頻道列表
				const query = sb.groupChannel.createMyGroupChannelListQuery({
					includeEmpty: true,
					includeMember: true,
					limit: 20
				})

				const channels = await query.next()
				this.groupChannels = channels

				// 設定 channel handler（監聽新訊息和新 channel）
				this.setupChannelHandler()

				// 自動選擇第一個頻道並載入訊息
				if (this.groupChannels.length > 0) {
					await this.selectChannel(this.groupChannels[0])
				}
			} catch (error) {
				console.error('Failed to get group channels:', error)
			}
		},
		async handleTargetUser() {
			const targetUserId = localStorage.getItem('TARGET_USER_ID')
			if (!targetUserId) return

			// 清除 localStorage
			localStorage.removeItem('TARGET_USER_ID')

			const currentUserId = this.user.userDto?.id as string
			if (!currentUserId || targetUserId === currentUserId) return

			try {
				// 檢查是否已有與該用戶的 channel
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const existingChannel = this.groupChannels.find((channel: any) => {
					const members = channel.members || []
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					return members.some((m: any) => m.userId === targetUserId)
				})

				if (existingChannel) {
					// 已有 channel，直接選擇
					await this.selectChannel(existingChannel)
				} else {
					// 沒有 channel，先加入好友
					await this.api$.userFriendApi.addFriend(currentUserId, targetUserId)

					// 創建新的 group channel
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					const sb = this.$sendbird.get() as any
					if (!sb) return

					const params = {
						invitedUserIds: [targetUserId],
						isDistinct: true // 確保相同成員組合只有一個 channel
					}
					const newChannel = await sb.groupChannel.createChannel(params)

					// 檢查頻道是否已存在於列表中（isDistinct 可能返回現有頻道）
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					const existsInList = this.groupChannels.some((c: any) => c.url === newChannel.url)
					if (!existsInList) {
						// 加入到頻道列表最前面
						this.groupChannels.unshift(newChannel)
					}

					// 選擇該 channel
					await this.selectChannel(newChannel)
				}
			} catch (error) {
				console.error('Failed to handle target user:', error)
			}
		}
	},
	async created() {
		this.loading = true
		try {
			await this.getGroupChannelByUserId()
			// 處理從 profile-sharing 頁面登入後導向的情況
			await this.handleTargetUser()
		} finally {
			this.loading = false
		}
	},
	beforeDestroy() {
		// 清理 channel handler
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const sb = this.$sendbird.get() as any
		if (sb) {
			sb.groupChannel.removeGroupChannelHandler(this.channelHandlerId)
		}
	}
})
